<template>
  <div class="fusion-preview">
    <!-- Page header -->
    <div class="page-header">
      <v-btn class="back-btn" @click="handleBack">
        <v-icon left>fa-caret-left</v-icon>RETOUR
      </v-btn>
      <h1>{{ step === 'recap' ? 'RÉCAPITULATIF DE LA FUSION' : 'APERÇU DE FUSION' }}</h1>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="#0b87ba" size="64" />
      <p class="mt-4 grey--text">Chargement des données…</p>
    </div>

    <template v-else>
      <!-- ── STEP 1 : comparison ───────────────────────────────────────────── -->
      <template v-if="step === 'compare'">
        <!-- Identity banner -->
        <v-container fluid class="px-6 pt-4 pb-0">
          <v-row>
            <v-col cols="6">
              <div class="identity-card identity-card--source">
                <div class="identity-badge identity-badge--source">SERA DÉSACTIVÉ</div>
                <div class="identity-name">{{ source.raison_sociale }}</div>
                <div class="identity-type">
                  {{ source.StatutEntreprise ? source.StatutEntreprise.statut_entreprise : '' }}
                  <span v-if="source.code_client_fta" class="ml-2 grey--text text--darken-1">
                    ({{ source.code_client_fta }})
                  </span>
                </div>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="identity-card identity-card--target">
                <div class="identity-badge identity-badge--target">SERA CONSERVÉ</div>
                <div class="identity-name">{{ target.raison_sociale }}</div>
                <div class="identity-type">
                  {{ target.StatutEntreprise ? target.StatutEntreprise.statut_entreprise : '' }}
                  <span v-if="target.code_client_fta" class="ml-2 grey--text text--darken-1">
                    ({{ target.code_client_fta }})
                  </span>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>

        <v-container fluid class="px-6">
          <!-- Informations générales -->
          <div class="section-title mt-4">
            INFORMATIONS GÉNÉRALES
            <span class="section-hint">Cliquez sur une valeur pour la sélectionner en cas de conflit</span>
          </div>
          <v-simple-table class="info-table mb-6">
            <template v-slot:default>
              <thead>
                <tr>
                  <th scope="col" class="col-field">CHAMP</th>
                  <th scope="col" class="col-source">
                    SOURCE <span class="deactivated-label">(sera supprimé)</span>
                  </th>
                  <th scope="col" class="col-target">
                    CIBLE <span class="kept-label">(sera conservé)</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="field in infoFields" :key="field.key" :class="{ 'row-conflict': hasConflict(field.key) }">
                  <td class="field-label">{{ field.label }}</td>
                  <!-- source cell -->
                  <td
                    :class="conflictCellClass(field.key, 'source')"
                    @click="hasConflict(field.key) && selectChoice(field.key, 'source')"
                  >
                    <v-icon v-if="hasConflict(field.key) && choices[field.key] === 'source'" small class="check-icon">fa-check-circle</v-icon>
                    {{ displayField(source, field) }}
                  </td>
                  <!-- target cell -->
                  <td
                    :class="conflictCellClass(field.key, 'target')"
                    @click="hasConflict(field.key) && selectChoice(field.key, 'target')"
                  >
                    <v-icon v-if="hasConflict(field.key) && choices[field.key] === 'target'" small class="check-icon">fa-check-circle</v-icon>
                    {{ displayField(target, field) }}
                  </td>
                </tr>
                <!-- commerciaux row -->
                <tr :class="{ 'row-conflict': commerciauxConflict }">
                  <td class="field-label">COMMERCIAUX</td>
                  <td
                    :class="conflictCellClass('commerciaux', 'source')"
                    @click="commerciauxConflict && selectChoice('commerciaux', 'source')"
                  >
                    <v-icon v-if="commerciauxConflict && choices['commerciaux'] !== 'target'" small class="check-icon">fa-check-circle</v-icon>
                    {{ formatCommerciaux(source.Users) }}
                  </td>
                  <td
                    :class="conflictCellClass('commerciaux', 'target')"
                    @click="commerciauxConflict && selectChoice('commerciaux', 'target')"
                  >
                    <v-icon v-if="commerciauxConflict && choices['commerciaux'] !== 'source'" small class="check-icon">fa-check-circle</v-icon>
                    {{ formatCommerciaux(target.Users) }}
                  </td>
                </tr>
                <tr v-if="commerciauxConflict">
                  <td class="field-label"></td>
                  <td colspan="2">
                    <v-btn-toggle v-model="choices.commerciaux" mandatory dense color="primary">
                      <v-btn small value="both">Garder les deux</v-btn>
                      <v-btn small value="source">Source seulement</v-btn>
                      <v-btn small value="target">Cible seulement</v-btn>
                    </v-btn-toggle>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!-- Data sections -->
          <template v-for="section in dataSections">
            <div :key="section.key" class="mb-6">
              <div class="section-title">{{ section.label }}</div>
              <v-row>
                <v-col cols="6">
                  <div class="side-label side-label--source">
                    SOURCE — {{ sectionData[section.key].source.length }} enregistrement(s)
                    <span v-if="sectionData[section.key].sourceTotal > sectionData[section.key].source.length" class="more-label">
                      ({{ sectionData[section.key].sourceTotal }} au total)
                    </span>
                  </div>
                  <v-data-table
                    :headers="section.headers"
                    :items="sectionData[section.key].source"
                    :items-per-page="10"
                    dense
                    class="side-table"
                    no-data-text="Aucun enregistrement"
                    :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
                  />
                </v-col>
                <v-col cols="6">
                  <div class="side-label side-label--target">
                    CIBLE — {{ sectionData[section.key].target.length }} enregistrement(s)
                    <span v-if="sectionData[section.key].targetTotal > sectionData[section.key].target.length" class="more-label">
                      ({{ sectionData[section.key].targetTotal }} au total)
                    </span>
                  </div>
                  <v-data-table
                    :headers="section.headers"
                    :items="sectionData[section.key].target"
                    :items-per-page="10"
                    dense
                    class="side-table"
                    no-data-text="Aucun enregistrement"
                    :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
                  />
                </v-col>
              </v-row>
            </div>
          </template>

          <!-- Documents -->
          <div class="mb-6">
            <div class="section-title">DOCUMENTS</div>
            <v-row>
              <v-col cols="6">
                <div class="side-label side-label--source">SOURCE</div>
                <div class="doc-panel">
                  <div v-if="docs.source.dirs.length > 0" class="mb-2">
                    <span class="doc-category-label">Dossiers :</span>
                    <span>{{ docs.source.dirs.join(', ') }}</span>
                  </div>
                  <div>
                    <span class="doc-category-label">Fichiers :</span>
                    <span>{{ docs.source.files.length }} fichier(s)</span>
                  </div>
                  <ul v-if="docs.source.files.length > 0" class="file-list mt-1">
                    <li v-for="f in docs.source.files" :key="f.name || f">{{ f.name || f }}</li>
                  </ul>
                  <p v-if="docs.source.dirs.length === 0 && docs.source.files.length === 0" class="grey--text mt-2">Aucun document</p>
                </div>
              </v-col>
              <v-col cols="6">
                <div class="side-label side-label--target">CIBLE</div>
                <div class="doc-panel">
                  <div v-if="docs.target.dirs.length > 0" class="mb-2">
                    <span class="doc-category-label">Dossiers :</span>
                    <span>{{ docs.target.dirs.join(', ') }}</span>
                  </div>
                  <div>
                    <span class="doc-category-label">Fichiers :</span>
                    <span>{{ docs.target.files.length }} fichier(s)</span>
                  </div>
                  <ul v-if="docs.target.files.length > 0" class="file-list mt-1">
                    <li v-for="f in docs.target.files" :key="f.name || f">{{ f.name || f }}</li>
                  </ul>
                  <p v-if="docs.target.dirs.length === 0 && docs.target.files.length === 0" class="grey--text mt-2">Aucun document</p>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-container>

        <!-- Action bar -->
        <div class="action-bar">
          <v-btn text large @click="$router.go(-1)">Annuler</v-btn>
          <v-btn color="#0b87ba" dark depressed large @click="step = 'recap'">
            Voir le récapitulatif
            <v-icon right small>fa-arrow-right</v-icon>
          </v-btn>
        </div>
      </template>

      <!-- ── STEP 2 : recap ────────────────────────────────────────────────── -->
      <template v-else-if="step === 'recap'">
        <v-container fluid class="px-6 pt-6">

          <!-- Merged entity header -->
          <div class="recap-entity-header mb-4">
            <div class="identity-badge identity-badge--target mr-3">ENTITÉ RÉSULTANTE</div>
            <span class="recap-entity-name">{{ mergedEntity.raison_sociale }}</span>
          </div>

          <!-- Merged info table -->
          <div class="section-title">INFORMATIONS DE L'ENTITÉ RÉSULTANTE</div>
          <v-simple-table class="info-table mb-6">
            <template v-slot:default>
              <thead>
                <tr>
                  <th scope="col" class="col-field">CHAMP</th>
                  <th scope="col" style="width:55%">VALEUR RETENUE</th>
                  <th scope="col" style="width:20%">ORIGINE</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="field in infoFields" :key="field.key">
                  <td class="field-label">{{ field.label }}</td>
                  <td>{{ displayField(mergedEntity, field) }}</td>
                  <td>
                    <span v-if="hasConflict(field.key)" :class="originBadgeClass(field.key)">
                      {{ choices[field.key] === 'source' ? 'SOURCE' : 'CIBLE' }}
                    </span>
                    <span v-else class="origin-badge origin-badge--target">CIBLE</span>
                  </td>
                </tr>
                <tr>
                  <td class="field-label">COMMERCIAUX</td>
                  <td>{{ formatCommerciaux(mergedEntity.Users) }}</td>
                  <td>
                    <span v-if="commerciauxConflict" :class="originBadgeClass('commerciaux')">
                      {{ commerciauxOriginLabel }}
                    </span>
                    <span v-else class="origin-badge origin-badge--target">{{ commerciauxOriginLabel }}</span>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!-- Transfer summary -->
          <div class="section-title">DONNÉES QUI SERONT TRANSFÉRÉES</div>
          <v-row class="mb-6">
            <v-col
              v-for="card in transferCards"
              :key="card.key"
              cols="12" sm="6" md="3"
            >
              <div class="transfer-card">
                <div class="transfer-card__label">{{ card.label }}</div>
                <div class="transfer-card__counts">
                  <span class="count-source">{{ card.sourceCount }}</span>
                  <span class="count-sep">+</span>
                  <span class="count-target">{{ card.targetCount }}</span>
                  <span class="count-sep">=</span>
                  <span class="count-total">{{ card.sourceCount + card.targetCount }}</span>
                </div>
                <div class="transfer-card__legend">
                  <span class="legend-source">source</span>
                  <span class="legend-target">cible</span>
                  <span class="legend-total">total</span>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- Documents recap -->
          <div class="section-title">DOCUMENTS</div>
          <v-row class="mb-6">
            <v-col cols="6">
              <div class="doc-recap-card doc-recap-card--source">
                <strong>SOURCE</strong> — {{ docs.source.files.length }} fichier(s), {{ docs.source.dirs.length }} dossier(s)
              </div>
            </v-col>
            <v-col cols="6">
              <div class="doc-recap-card doc-recap-card--target">
                <strong>CIBLE</strong> — {{ docs.target.files.length }} fichier(s), {{ docs.target.dirs.length }} dossier(s)
              </div>
            </v-col>
          </v-row>

        </v-container>

        <!-- Action bar -->
        <div class="action-bar">
          <v-btn text large @click="step = 'compare'">
            <v-icon left small>fa-arrow-left</v-icon>
            Modifier les choix
          </v-btn>
          <v-btn color="#43a047" dark depressed large :loading="confirming" @click="confirmFusion">
            Confirmer la fusion
          </v-btn>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import apiEntreprise from '@/api/entreprise';
import apiPersonne from '@/api/personne';

export default {
  name: 'FusionPreview',
  data() {
    return {
      loading: true,
      confirming: false,
      step: 'compare',
      choices: {},
      source: {},
      target: {},
      sectionData: {
        contacts:      { source: [], target: [], sourceTotal: 0, targetTotal: 0 },
        rapports:      { source: [], target: [], sourceTotal: 0, targetTotal: 0 },
        projetsActive: { source: [], target: [], sourceTotal: 0, targetTotal: 0 },
        projetsClosed: { source: [], target: [], sourceTotal: 0, targetTotal: 0 },
      },
      docs: {
        source: { dirs: [], files: [] },
        target: { dirs: [], files: [] },
      },
    };
  },
  computed: {
    targetId() { return this.$route.params.id_entreprise; },
    sourceId() { return this.$route.params.source_id; },
    infoFields() {
      return [
        { label: 'RAISON SOCIALE',     key: 'raison_sociale' },
        { label: 'ADRESSE',            key: 'adresse' },
        { label: 'CODE POSTAL',        key: 'code_postal' },
        { label: 'VILLE',              key: 'ville' },
        { label: 'EMAIL',              key: 'email_entreprise' },
        { label: 'TÉLÉPHONE',          key: 'telephone_fixe' },
        { label: 'TYPE DE CLIENT',     key: 'type_client' },
        { label: "POTENTIEL D'ACHAT",  key: 'potentiel_achat' },
      ];
    },
    dataSections() {
      return [
        {
          key: 'contacts',
          label: 'CONTACTS',
          headers: [
            { text: 'NOM',    value: 'nom',    sortable: true },
            { text: 'PRÉNOM', value: 'prenom', sortable: true },
            { text: 'EMAIL',  value: 'email_professionnel',    sortable: false },
            { text: 'TÉL.',   value: 'telephone_professionnel', sortable: false },
          ],
        },
        {
          key: 'rapports',
          label: 'RAPPORTS',
          headers: [
            { text: 'DATE',        value: 'date_rapport', sortable: true },
            { text: 'APPEL',       value: 'appel',        sortable: false },
            { text: 'VISITE',      value: 'visite',       sortable: false },
            { text: 'DESCRIPTION', value: 'description',  sortable: false },
          ],
        },
        {
          key: 'projetsActive',
          label: 'PROJETS EN COURS',
          headers: [
            { text: 'NOM',         value: 'nom_projet',  sortable: true },
            { text: 'DATE',        value: 'date_projet', sortable: true },
            { text: 'DESCRIPTION', value: 'description', sortable: false },
          ],
        },
        {
          key: 'projetsClosed',
          label: 'PROJETS TERMINÉS',
          headers: [
            { text: 'NOM',           value: 'nom_projet',    sortable: true },
            { text: 'DATE',          value: 'date_projet',   sortable: true },
            { text: 'PROJET OBTENU', value: 'projet_obtenu', sortable: false },
            { text: 'DESCRIPTION',   value: 'description',   sortable: false },
          ],
        },
      ];
    },
    // Compare commerciaux by id, not by the displayed names (order and homonyms)
    commerciauxConflict() {
      const s = this.userIds(this.source.Users);
      const t = this.userIds(this.target.Users);
      return s.length > 0 && t.length > 0 && s.join(',') !== t.join(',');
    },
    // Mirrors the API: source only, target only, otherwise the union of both
    mergedUsers() {
      const choice = this.commerciauxConflict ? this.choices['commerciaux'] : 'both';
      if (choice === 'source') { return this.source.Users || []; }
      if (choice === 'target') { return this.target.Users || []; }
      const byId = new Map();
      for (const u of [...(this.target.Users || []), ...(this.source.Users || [])]) {
        byId.set(u.id_utilisateur, u);
      }
      return [...byId.values()];
    },
    commerciauxOriginLabel() {
      const choice = this.commerciauxConflict ? this.choices['commerciaux'] : 'both';
      if (choice === 'source') { return 'SOURCE'; }
      if (choice === 'target') { return 'CIBLE'; }
      return (this.source.Users || []).length > 0 ? 'SOURCE + CIBLE' : 'CIBLE';
    },
    mergedEntity() {
      const result = { ...this.target };
      for (const field of this.infoFields) {
        if (this.hasConflict(field.key) && this.choices[field.key] === 'source') {
          result[field.key] = this.source[field.key];
        }
      }
      result.Users = this.mergedUsers;
      return result;
    },
    transferCards() {
      return [
        {
          key: 'contacts',
          label: 'CONTACTS',
          sourceCount: this.sectionData.contacts.sourceTotal,
          targetCount: this.sectionData.contacts.targetTotal,
        },
        {
          key: 'rapports',
          label: 'RAPPORTS',
          sourceCount: this.sectionData.rapports.sourceTotal,
          targetCount: this.sectionData.rapports.targetTotal,
        },
        {
          key: 'projetsActive',
          label: 'PROJETS EN COURS',
          sourceCount: this.sectionData.projetsActive.sourceTotal,
          targetCount: this.sectionData.projetsActive.targetTotal,
        },
        {
          key: 'projetsClosed',
          label: 'PROJETS TERMINÉS',
          sourceCount: this.sectionData.projetsClosed.sourceTotal,
          targetCount: this.sectionData.projetsClosed.targetTotal,
        },
      ];
    },
  },
  async created() {
    try {
      await Promise.all([
        this.loadEntreprise(this.targetId, 'target'),
        this.loadEntreprise(this.sourceId, 'source'),
      ]);
      await Promise.all([
        this.loadContacts(this.targetId, 'target'),
        this.loadContacts(this.sourceId, 'source'),
        this.loadRapports(this.targetId, 'target'),
        this.loadRapports(this.sourceId, 'source'),
        this.loadProjetsActive(this.targetId, 'target'),
        this.loadProjetsActive(this.sourceId, 'source'),
        this.loadProjetsClosed(this.targetId, 'target'),
        this.loadProjetsClosed(this.sourceId, 'source'),
        this.loadDocuments(this.targetId, 'target'),
        this.loadDocuments(this.sourceId, 'source'),
      ]);
      this.initChoices();
    } catch {
      this.$store.dispatch('notify', { message: 'Erreur lors du chargement des données' });
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async confirmFusion() {
      this.confirming = true;
      try {
        const fieldOverrides = {};
        for (const field of this.infoFields) {
          if (this.hasConflict(field.key) && this.choices[field.key] === 'source') {
            fieldOverrides[field.key] = this.source[field.key];
          }
        }
        // 'both' (or no conflict) is sent as null: the API keeps the union
        const choice = this.choices['commerciaux'];
        const commerciauxChoice = this.commerciauxConflict && choice !== 'both' ? choice : null;
        const res = await apiEntreprise.mergeEntreprises(this.sourceId, this.targetId, fieldOverrides, commerciauxChoice);
        this.$store.dispatch('notify', { message: res.data.message || 'Fusion effectuée avec succès', type: 'success' });
        this.$router.push({ name: 'client', params: { id_entreprise: this.targetId } });
      } catch {
        this.$store.dispatch('notify', { message: 'Erreur lors de la fusion' });
        this.confirming = false;
      }
    },
    handleBack() {
      if (this.step === 'recap') {
        this.step = 'compare';
      } else {
        this.$router.go(-1);
      }
    },
    initChoices() {
      const choices = {};
      for (const field of this.infoFields) {
        choices[field.key] = 'target';
      }
      choices.commerciaux = 'both';
      this.choices = choices;
    },
    selectChoice(key, side) {
      this.choices = { ...this.choices, [key]: side };
    },
    conflictCellClass(key, side) {
      const isConflict = key === 'commerciaux' ? this.commerciauxConflict : this.hasConflict(key);
      if (!isConflict) { return ''; }
      const chosen = this.choices[key] === side || this.choices[key] === 'both';
      return chosen ? 'cell-chosen' : 'cell-unchosen';
    },
    originBadgeClass(key) {
      const side = this.choices[key];
      return side === 'source' || side === 'both' ? 'origin-badge origin-badge--source' : 'origin-badge origin-badge--target';
    },
    async loadEntreprise(id, side) {
      const res = await apiEntreprise.getUserEntreprise(id);
      this[side] = res.data.entreprise;
    },
    async loadContacts(id, side) {
      const res = await apiPersonne.getAllEntrepriseContacts('', id);
      const rows = res.data.personne.rows;
      this.sectionData.contacts[side] = rows;
      this.sectionData.contacts[side + 'Total'] = res.data.personne.totalItems || rows.length;
    },
    async loadRapports(id, side) {
      const res = await apiEntreprise.getAllEntrepriseRapports(id);
      const rows = res.data.rapports.rows;
      this.sectionData.rapports[side] = rows;
      this.sectionData.rapports[side + 'Total'] = res.data.rapports.totalItems || rows.length;
    },
    async loadProjetsActive(id, side) {
      try {
        const res = await apiEntreprise.getAllEntrepriseProjetsActive(id);
        const rows = res.data.projet.rows;
        this.sectionData.projetsActive[side] = rows;
        this.sectionData.projetsActive[side + 'Total'] = res.data.projet.totalItems || rows.length;
      } catch { /* no active projets for this company */ }
    },
    async loadProjetsClosed(id, side) {
      try {
        const res = await apiEntreprise.getAllEntrepriseProjetsClosed(id);
        const rows = res.data.projet.rows;
        this.sectionData.projetsClosed[side] = rows;
        this.sectionData.projetsClosed[side + 'Total'] = res.data.projet.totalItems || rows.length;
      } catch { /* no closed projets for this company */ }
    },
    async loadDocuments(id, side) {
      try {
        const res = await apiEntreprise.getEntrepriseDocumentsAndDirectory('', id, 'entreprises', true, null);
        this.docs[side].dirs = res.data.dirs || [];
        this.docs[side].files = res.data.documents || [];
      } catch { /* no document directory for this company */ }
    },
    hasConflict(key) {
      const sv = this.source[key];
      const tv = this.target[key];
      return sv && tv && String(sv).trim() !== String(tv).trim();
    },
    displayField(obj, field) {
      const v = obj[field.key];
      if (v === null || v === undefined || v === '') { return '—'; }
      return v;
    },
    userIds(users) {
      return (users || []).map(u => u.id_utilisateur).sort((a, b) => a - b);
    },
    formatCommerciaux(users) {
      if (!users || users.length === 0) { return '—'; }
      return users.map(u => [u.prenom, u.nom].filter(Boolean).join(' ')).join(', ');
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables.scss";

.fusion-preview {
  min-height: 100vh;
  background-color: #f5f5f5;
  padding-bottom: 80px;
}

// ── Header ──────────────────────────────────────────────────────────────────
.page-header {
  background: linear-gradient(270deg, rgba(2,0,36,1) 0%, rgba(11,135,176,1) 80%, rgba(11,135,186,1) 100%);
  display: flex;
  align-items: center;
  height: 56px;

  h1 {
    color: #fff;
    font-size: 1.5em;
    font-weight: 900;
    margin: 0;
    padding-left: 40px;
  }
}

.back-btn {
  background-color: #55aacf !important;
  color: #fff !important;
  font-size: 1.1em;
  font-weight: bold;
  border-radius: 0 40px 40px 0;
  height: 100% !important;
  min-width: 110px !important;
}

// ── Identity cards ───────────────────────────────────────────────────────────
.identity-card {
  border-radius: 8px;
  padding: 16px 20px;
  border-left: 6px solid;

  &--source { border-color: #e53935; background-color: #fff5f5; }
  &--target { border-color: #43a047; background-color: #f5fff5; }
}

.identity-badge {
  font-size: 0.7rem;
  font-weight: bold;
  letter-spacing: 0.08em;
  padding: 2px 8px;
  border-radius: 20px;
  display: inline-block;
  margin-bottom: 8px;

  &--source { background-color: #e53935; color: white; }
  &--target { background-color: #43a047; color: white; }
}

.identity-name { font-size: 1.2rem; font-weight: 700; color: #222; }
.identity-type  { font-size: 0.85rem; color: #666; text-transform: capitalize; margin-top: 2px; }

// ── Section titles ───────────────────────────────────────────────────────────
.section-title {
  background-color: #0b87ba;
  color: white;
  font-weight: bold;
  padding: 8px 14px;
  border-radius: 4px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 12px;

  .section-hint {
    font-size: 0.72rem;
    font-weight: normal;
    opacity: 0.85;
  }
}

// ── Info comparison table ────────────────────────────────────────────────────
.info-table {
  border: 1px solid #e0e0e0;
  border-radius: 4px;

  th {
    background-color: #f0f0f0 !important;
    font-weight: bold;
    font-size: 0.78rem;
  }

  .col-field  { width: 18%; }
  .col-source { width: 41%; }
  .col-target { width: 41%; }

  .field-label {
    font-weight: 600;
    color: #555;
    font-size: 0.78rem;
    text-transform: uppercase;
  }

  .deactivated-label { color: #e53935; font-size: 0.75rem; }
  .kept-label        { color: #43a047; font-size: 0.75rem; }

  .row-conflict > td:not(.field-label) { background-color: #fff8e1; }
}

// ── Conflict cell selectors ──────────────────────────────────────────────────
.cell-chosen {
  background-color: #1565c0 !important;
  color: white !important;
  cursor: pointer;
  font-weight: 600;

  .check-icon { color: white !important; margin-right: 4px; }
}

.cell-unchosen {
  opacity: 0.45;
  cursor: pointer;

  &:hover { opacity: 0.7; background-color: #e3f2fd; }
}

.check-icon { color: #1565c0; margin-right: 4px; }

// ── Side-by-side data tables ─────────────────────────────────────────────────
.side-label {
  font-size: 0.78rem;
  font-weight: bold;
  padding: 6px 10px;
  border-radius: 4px 4px 0 0;
  color: white;

  &--source { background-color: #c62828; }
  &--target { background-color: #2e7d32; }

  .more-label { font-weight: normal; font-size: 0.72rem; opacity: 0.9; }
}

.side-table { border: 1px solid #e0e0e0; border-top: none; }

// ── Documents panel ──────────────────────────────────────────────────────────
.doc-panel {
  border: 1px solid #e0e0e0;
  border-top: none;
  border-radius: 0 0 4px 4px;
  padding: 12px 14px;
  background: white;
  min-height: 60px;

  .doc-category-label { font-weight: 600; margin-right: 6px; }
}

.file-list {
  padding-left: 18px;
  margin: 4px 0 0 0;
  font-size: 0.85rem;
  color: #444;

  li { margin-bottom: 2px; }
}

// ── Action bar ───────────────────────────────────────────────────────────────
.action-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #e0e0e0;
  padding: 12px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 100;
  box-shadow: 0 -2px 8px rgba(0,0,0,0.08);

  ::v-deep .v-btn { letter-spacing: 0; text-transform: none; font-weight: 600; }
}

// ── Recap ────────────────────────────────────────────────────────────────────
.recap-entity-header {
  display: flex;
  align-items: center;
}

.recap-entity-name {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a1a;
}

.origin-badge {
  font-size: 0.7rem;
  font-weight: bold;
  letter-spacing: 0.06em;
  padding: 2px 7px;
  border-radius: 10px;

  &--source { background-color: #e53935; color: white; }
  &--target { background-color: #43a047; color: white; }
}

.transfer-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  text-align: center;

  &__label {
    font-size: 0.72rem;
    font-weight: bold;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 10px;
  }

  &__counts {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 1.4rem;
    font-weight: 700;
  }

  &__legend {
    display: flex;
    justify-content: center;
    gap: 12px;
    font-size: 0.68rem;
    color: #999;
    margin-top: 4px;
  }
}

.count-source { color: #c62828; }
.count-target { color: #2e7d32; }
.count-total  { color: #0b87ba; }
.count-sep    { color: #bbb; font-weight: 400; font-size: 1rem; }

.legend-source { color: #c62828; }
.legend-target { color: #2e7d32; }
.legend-total  { color: #0b87ba; }

.doc-recap-card {
  border-radius: 8px;
  padding: 14px 18px;
  border-left: 4px solid;
  background: white;

  &--source { border-color: #c62828; }
  &--target { border-color: #2e7d32; }
}
</style>
